// Utilisation Formula Settings Storage
// Manages the company-wide utilisation formula type preference.
//
// Phase 2: API-backed with module-level cache for synchronous reads.
// Falls back to defaults if the API is unavailable.

import { apiFetch } from './api-error-handler';
import { getApiBaseUrl } from './data-service';

export type UtilisationFormulaType = 'ActualVsBudgeted' | 'ActualVsAllocated' | 'BillableVsCapacity';

export interface UtilisationSettings {
  formulaType: UtilisationFormulaType;
  lastUpdatedBy?: string;
  lastUpdatedAt?: string;
}

export const DEFAULT_UTILISATION_SETTINGS: UtilisationSettings = {
  formulaType: 'ActualVsBudgeted',
};

// ── Module-level cache ──────────────────────────────────────────────────────
let _cache: UtilisationSettings = { ...DEFAULT_UTILISATION_SETTINGS };
let _hasFetched = false;

/**
 * Synchronous read — returns the cached utilisation settings.
 */
export function getUtilisationSettings(): UtilisationSettings {
  return _cache;
}

/**
 * Fetch utilisation settings from the server and update the cache.
 */
export async function fetchUtilisationSettings(): Promise<UtilisationSettings> {
  try {
    const baseUrl = getApiBaseUrl();
    const data = await apiFetch<UtilisationSettings>(
      `${baseUrl}/settings/utilisation-formula`
    );
    _cache = { ...DEFAULT_UTILISATION_SETTINGS, ...data };
    _hasFetched = true;
  } catch (error) {
    console.error('Error fetching utilisation settings from API:', error);
    if (!_hasFetched) {
      _cache = { ...DEFAULT_UTILISATION_SETTINGS };
    }
  }
  return _cache;
}

/**
 * Compute utilisation rate based on the selected formula type.
 * Falls back to ActualVsBudgeted when the required fields are missing.
 */
export function computeUtilisationRate(
  project: { budgetHours?: number | null; actualHours?: number | null; allocatedHours?: number | null; billableHours?: number | null; capacityHours?: number | null },
  formulaType: UtilisationFormulaType
): number {
  const actual = project.actualHours ?? 0;
  const budget = project.budgetHours ?? 0;

  switch (formulaType) {
    case 'ActualVsAllocated': {
      const allocated = project.allocatedHours ?? 0;
      if (allocated > 0) return (actual / allocated) * 100;
      // fall back to ActualVsBudgeted when allocated data unavailable
      return budget > 0 ? (actual / budget) * 100 : 0;
    }
    case 'BillableVsCapacity': {
      const billable = project.billableHours ?? 0;
      const capacity = project.capacityHours ?? 0;
      if (capacity > 0) return (billable / capacity) * 100;
      // fall back to ActualVsBudgeted when capacity data unavailable
      return budget > 0 ? (actual / budget) * 100 : 0;
    }
    case 'ActualVsBudgeted':
    default:
      return budget > 0 ? (actual / budget) * 100 : 0;
  }
}

/**
 * Save utilisation settings to the server and update the cache.
 */
export async function saveUtilisationSettings(formulaType: UtilisationFormulaType): Promise<void> {
  const baseUrl = getApiBaseUrl();
  const data = await apiFetch<UtilisationSettings>(
    `${baseUrl}/settings/utilisation-formula`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ formulaType }),
    }
  );
  _cache = { ...DEFAULT_UTILISATION_SETTINGS, ...data };
  _hasFetched = true;
}
